import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { isEmpty, lowerCase, startCase, union } from 'lodash'
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis, ResponsiveContainer } from 'recharts'

import {
  fetchDemurragePerProduct,
  fetchDemurragePerVessel,
  fetchKpiOrderDurationAdherence,
  fetchKpiOrderStartTimeAdherence,
  fetchProductAmountDiff,
  fetchProductDelays
} from '../../../js/rest'
import { handleFetchError } from '../../../js/sentry'
import { chartMargins } from '../../../utils/renderUtils'
import KpiBarchartTooltip from '../kpiBarchartTooltip/KpiBarchartTooltip'
import { VISIT_DELAY_COLORS } from '../../../js/constants'
import SpinnerOverlay from '../../spinnerOverlay/SpinnerOverlay'
import { formatPlanningAdherenceData } from '../../../utils/kpiUtils'
import FilterableBarChart from '../kpiCharts/filterableBarChart/FilterableBarChart'
import EmptyPlaceholderChart from '../kpiCharts/emptyPlaceholderChart/EmptyPlaceholderChart'

/**
 * Get and format duration adherence data
 */
async function fetchDurationAdherence({ terminalId, selection, authToken, customFetch }) {
  try {
    const durationAdherenceData = await fetchKpiOrderDurationAdherence(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )

    return formatPlanningAdherenceData(durationAdherenceData)
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

/**
 * Get and format start time adherence data
 */
async function getStartTimeAdherence({ terminalId, selection, authToken, customFetch }) {
  try {
    const startTimeAdherence = await fetchKpiOrderStartTimeAdherence(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )
    return formatPlanningAdherenceData(startTimeAdherence)
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

async function getAverageProductDelay({ terminalId, selection, authToken, customFetch }) {
  try {
    const avgProductDelay = await fetchProductDelays(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )

    const data = avgProductDelay
      .filter(({ averageDelayMillis }) => averageDelayMillis > 0)
      .map(({ productName, averageDelayMillis }) => ({
        product: productName,
        hours: Math.ceil(moment.duration(averageDelayMillis).asHours())
      }))

    const productList = union(avgProductDelay.map(p => p.productName))

    return { data, productList }
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

async function getProductAmountDiff({ terminalId, selection, authToken, customFetch }) {
  try {
    const productAmountDiff = await fetchProductAmountDiff(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )
    const data = productAmountDiff.map(p => ({ name: p.name, amount: Math.round(p.amount) }))
    const productList = union(productAmountDiff.map(p => p.name))

    return { data, productList }
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

async function getDemurragePerProductData({ terminalId, selection, authToken, customFetch }) {
  try {
    const demurragePerProduct = await fetchDemurragePerProduct(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )
    const data = demurragePerProduct
      .filter(({ demurrageInCents }) => demurrageInCents > 0)
      .map(({ productName, demurrageInCents }) => ({
        product: productName,
        demurrage: Math.ceil(demurrageInCents / 100)
      }))

    const productList = union(demurragePerProduct.map(p => p.productName))

    return { data, productList }
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

async function getDemurragePerVesselData({ terminalId, selection, authToken, customFetch }) {
  try {
    const demurragePerVessel = await fetchDemurragePerVessel(
      terminalId,
      selection.startDate,
      selection.stopDate,
      authToken,
      customFetch
    )
    const data = demurragePerVessel
      .filter(({ demurrageInCents }) => demurrageInCents > 0)
      .map(({ name, demurrageInCents }) => ({
        name: `${startCase(lowerCase(name))}`,
        demurrage: Math.ceil(demurrageInCents / 100)
      }))

    const vesselList = union(demurragePerVessel.map(v => v.name))

    return { data, vesselList }
  } catch (err) {
    handleFetchError(err)
    return null
  }
}

/**
 * Tab content for KPI Adherence charts
 */
const KpiAdherence = ({ terminalId, selection, authToken, customFetch, productColors }) => {
  const [fetchingData, setFetchingData] = useState(false)
  const [durationAdherence, setDurationAdherence] = useState({ data: [], delayList: [] })
  const [startTimeAdherence, setStartTimeAdherence] = useState({ data: [], delayList: [] })
  const [productDelays, setProductDelays] = useState({ data: [], productList: [] })
  const [productAmountDiff, setProductAmountDiff] = useState({ data: [], productList: [] })
  const [productDemurrage, setProductDemurrage] = useState({ data: [], productList: [] })
  const [vesselDemurrage, setVesselDemurrage] = useState({ data: [], vesselList: [] })

  useEffect(() => {
    setFetchingData(true)

    Promise.all([
      fetchDurationAdherence({ terminalId, selection, authToken, customFetch }),
      getStartTimeAdherence({ terminalId, selection, authToken, customFetch }),
      getAverageProductDelay({ terminalId, selection, authToken, customFetch }),
      getProductAmountDiff({ terminalId, selection, authToken, customFetch }),
      getDemurragePerProductData({ terminalId, selection, authToken, customFetch }),
      getDemurragePerVesselData({ terminalId, selection, authToken, customFetch })
    ]).then(
      ([
        durationAdherenceData,
        startTimeAdherenceData,
        averageProductDelay,
        productAmountDiff,
        demurragePerProduct,
        demurragePerVessel
      ]) => {
        setFetchingData(false)
        setDurationAdherence(durationAdherenceData)
        setStartTimeAdherence(startTimeAdherenceData)
        setProductDelays(averageProductDelay)
        setProductAmountDiff(productAmountDiff)
        setProductDemurrage(demurragePerProduct)
        setVesselDemurrage(demurragePerVessel)
      }
    )
  }, [terminalId, authToken, selection, customFetch])

  const canRenderDurationAdherenceChart = durationAdherence && !isEmpty(durationAdherence.data)
  const canRenderStartTimeAdherenceChart = startTimeAdherence && !isEmpty(startTimeAdherence.data)
  const canRenderProductDelaysChart = productDelays && !isEmpty(productDelays.data)
  const canRenderProductAmountDiffs = productAmountDiff && !isEmpty(productAmountDiff.data)
  const canRenderDemurragePerProductChart = productDemurrage && !isEmpty(productDemurrage.data)
  const canRenderDemurragePerVesselChart = vesselDemurrage && !isEmpty(vesselDemurrage.data)

  return (
    <div className="kpi-chart-container">
      <div className="chart-group">
        <div className="chart-item">
          {canRenderDurationAdherenceChart ? (
            <FilterableBarChart
              chartMargins={chartMargins}
              data={durationAdherence.data}
              colors={VISIT_DELAY_COLORS}
              labels={durationAdherence.delayList}
              dataKey={'difference'}
              xAxisLabel={'Difference between planned and actual order duration (hours)'}
              yAxisLabel={'Number of orders'}
              maxBarSize={80}
              tooltipLabelSuffix={'hours'}
            />
          ) : (
            <EmptyPlaceholderChart
              xAxisLabel={'Difference between planned and actual order duration (hours)'}
              yAxisLabel={'Number of orders'}
            />
          )}
        </div>

        <div className="chart-item">
          {canRenderStartTimeAdherenceChart ? (
            <FilterableBarChart
              chartMargins={chartMargins}
              data={startTimeAdherence.data}
              colors={VISIT_DELAY_COLORS}
              labels={startTimeAdherence.delayList}
              dataKey={'difference'}
              xAxisLabel={'Difference between planned and actual order start times (hours)'}
              yAxisLabel={'Number of orders'}
              maxBarSize={80}
              tooltipLabelSuffix={'hours'}
            />
          ) : (
            <EmptyPlaceholderChart
              xAxisLabel={'Difference between planned and actual order start times (hours)'}
              yAxisLabel={'Number of orders'}
            />
          )}
        </div>

        <div className="chart-item">
          {canRenderProductDelaysChart ? (
            <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
              <BarChart data={productDelays.data} margin={chartMargins} layout="vertical">
                <CartesianGrid stroke="#f5f5f5" />
                <YAxis type="category" dataKey="product" width={135} />
                <XAxis type="number" label={{ value: 'Average order delay (hours)', position: 'bottom', offset: -5 }} />
                <Tooltip content={<KpiBarchartTooltip />} />

                <Bar dataKey="hours" name="Average delay" unit="hours" maxBarSize={80}>
                  {productDelays.productList.map((product, idx) => (
                    <Cell key={`cell-${idx}`} fill={productColors[product]} color={productColors[product]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPlaceholderChart xAxisLabel={'Average order delay (hours)'} yAxisLabel={'product'} />
          )}
        </div>

        <div className="chart-item">
          {canRenderProductAmountDiffs ? (
            <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
              <BarChart data={productAmountDiff.data} margin={chartMargins} layout="vertical">
                <CartesianGrid stroke="#f5f5f5" />
                <YAxis type="category" dataKey="name" width={135} />
                <XAxis
                  type="number"
                  label={{
                    value: 'Difference between planned and actual amounts (mt)',
                    position: 'bottom',
                    offset: -5
                  }}
                />
                <Tooltip content={<KpiBarchartTooltip />} />

                <Bar dataKey="amount" unit="mt" name="Amount difference" maxBarSize={80}>
                  {productAmountDiff.productList.map((product, idx) => (
                    <Cell key={`cell-${idx}`} fill={productColors[product]} color={productColors[product]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPlaceholderChart
              xAxisLabel={'Difference between planned and actual amounts (mt)'}
              yAxisLabel={'category'}
            />
          )}
        </div>

        <div className="chart-item">
          {canRenderDemurragePerProductChart ? (
            <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
              <BarChart data={productDemurrage.data} margin={chartMargins} layout="vertical">
                <CartesianGrid stroke="#f5f5f5" />
                <YAxis type="category" dataKey="product" width={135} />
                <XAxis
                  type="number"
                  label={{ value: 'Total demurrage charges per product (EUR)', position: 'bottom', offset: -5 }}
                />
                <Tooltip content={<KpiBarchartTooltip />} />

                <Bar dataKey="demurrage" name="Total charges" unit="€" maxBarSize={80}>
                  {productDemurrage.productList.map((product, idx) => (
                    <Cell key={`cell-${idx}`} fill={productColors[product]} color={productColors[product]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPlaceholderChart xAxisLabel={'Total demurrage charges per product (EUR)'} yAxisLabel={'category'} />
          )}
        </div>
        <div className="chart-item">
          {canRenderDemurragePerVesselChart ? (
            <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
              <BarChart data={vesselDemurrage.data} margin={chartMargins} layout="vertical">
                <CartesianGrid stroke="#f5f5f5" />
                <YAxis type="category" dataKey="name" width={135} />
                <XAxis
                  type="number"
                  label={{ value: 'Total demurrage charges per vessel (EUR)', position: 'bottom', offset: -5 }}
                />
                <Tooltip content={<KpiBarchartTooltip />} />

                <Bar dataKey="demurrage" name="Total charges" unit="€" maxBarSize={80}>
                  {vesselDemurrage.vesselList.map((vessel, idx) => (
                    <Cell key={`cell-${idx}`} fill="#66A62E" color="#66A62E" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPlaceholderChart xAxisLabel={'Total demurrage charges per vessel (EUR)'} yAxisLabel={'category'} />
          )}
        </div>
      </div>
      <SpinnerOverlay loading={fetchingData} />
    </div>
  )
}

KpiAdherence.propTypes = {
  authToken: PropTypes.string,
  customFetch: PropTypes.func.isRequired,
  terminalId: PropTypes.string.isRequired,
  productColors: PropTypes.object.isRequired,
  selection: PropTypes.shape({
    startDate: PropTypes.number.isRequired,
    stopDate: PropTypes.number.isRequired
  }).isRequired
}

export default KpiAdherence
